import React, { useState, useRef, useEffect, useMemo } from 'react';
import './gallerySection.css';
import Pagination from '../../../../Elements/Pagination/Pagination';
import Lightbox from '../../../../Layout/Lightbox/Lightbox';

const GallerySection = ({ data }) => {
  const {
    // title, 
    columns:
    defaultColumns, rowHeight, images
  } = data;

  // 1️⃣ Detectar móvil
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 2️⃣ Definir cuántas imágenes por columna y pageSize
  const imagesPerColumn = isMobile ? 1 : 3;
  const columns = defaultColumns;
  const pageSize = columns * imagesPerColumn;

  const handlePrev = () => setLightboxIdx(i => (i > 0 ? i - 1 : i));
  const handleNext = () => setLightboxIdx(i => (i < images.length - 1 ? i + 1 : i));
  const handleClose = () => setLightboxIdx(-1);

  // 3️⃣ Estado de paginación
  const [page, setPage] = useState(1);
  const totalPages = useMemo(
    () => Math.ceil(images.length / pageSize),
    [images.length, pageSize]
  );
  const startIdx = (page - 1) * pageSize;
  const currentImages = useMemo(
    () => images.slice(startIdx, startIdx + pageSize),
    [images, startIdx, pageSize]
  );

  // 4️⃣ Scroll suave al cambiar página
  const gridRef = useRef(null);
  const shouldScroll = useRef(false);
  useEffect(() => {
    if (shouldScroll.current && gridRef.current) {
      const y = gridRef.current.getBoundingClientRect().top + window.pageYOffset - 120;
      window.scrollTo({ top: y, behavior: 'smooth' });
      shouldScroll.current = false;
    }
  }, [page]);
  const goToPage = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) return;
    shouldScroll.current = true;
    setPage(newPage);
  };

  // 5️⃣ Ratios: en móvil cada columna tiene un solo ratio=[1]; desktop usa tu lógica original.
  const columnRatios = useMemo(() => {
    if (isMobile) {
      return Array.from({ length: columns }, () => [1]);
    }
    const minRatio = 0.20;
    const maxRatio = 0.60;
    const cols = [];
    for (let col = 0; col < columns; col++) {
      const colStart = col * 3;
      const imgsInThisCol = currentImages.slice(colStart, colStart + 3);
      if (imgsInThisCol.length < 3) {
        const arr = imgsInThisCol.map(() => 1 / 3);
        while (arr.length < 3) arr.push(0);
        cols.push(arr);
      } else {
        let h1, h2, h3, attempts = 0;
        do {
          const r1 = Math.random(), r2 = Math.random();
          const a = Math.min(r1, r2), b = Math.max(r1, r2);
          h1 = a; h2 = b - a; h3 = 1 - b;
          if (++attempts > 100) { h1 = h2 = h3 = 1 / 3; break; }
        } while (
          h1 < minRatio || h1 > maxRatio ||
          h2 < minRatio || h2 > maxRatio ||
          h3 < minRatio || h3 > maxRatio
        );
        cols.push([h1, h2, h3]);
      }
    }
    return cols;
  }, [currentImages, columns, isMobile]);

  return (
    <section className="gallery-section">
      <h2 className="gallery-title">Explore <br /><span>OUR WORK</span></h2>

      <div
        className="gallery-grid"
        ref={gridRef}
        style={{ height: `${rowHeight}px`, display: 'flex', gap: '15px', overflow: 'hidden' }}
      >
        {Array.from({ length: columns }).map((_, colIdx) => {
          const colStart = colIdx * imagesPerColumn;
          const imgsInThisCol = currentImages.slice(colStart, colStart + imagesPerColumn);
          const ratios = columnRatios[colIdx] || [];

          return (
            <div className="gallery-column" key={colIdx}>
              {imgsInThisCol.map((img, i) => {
                const realIdx = startIdx + colStart + i;
                return (
                  <div
                    key={i}
                    className="gallery-item-flex"
                    style={{
                      flexGrow: ratios[i] ?? 1,
                      flexBasis: 0,
                      backgroundImage: `url(${img.src})`
                    }}
                    role="img"
                    aria-label={img.alt || `imagen ${colStart + i + 1}`}
                    tabIndex={0}
                    onClick={() => setLightboxIdx(realIdx)}
                  />
                );
              })}

            </div>
          );
        })}
      </div>
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={goToPage}
      />
      {lightboxIdx !== -1 && (
        <Lightbox
          images={images}
          index={lightboxIdx}
          onClose={handleClose}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      )}
    </section>
  );
};

export default GallerySection;
